import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import api from "../../api";
import { IMAGE_URL } from "../../path";
import "../../styles/Login.scss";

interface Page {
    id: number;
    name: string;
    slug: string;
}

interface PageFeatureCard {
    id: number;
    page_id: number;
    title: string;
    image: string;
    body: string;
    icon: string | null;
    link: string | null;
    display_order: number;
    page: Page;
    created_at: string;
    updated_at: string;
}

interface EditableCard extends PageFeatureCard {
    newImage: File | null;
    newIcon: File | null;
}

const AdminHome = () => {
    const [cards, setCards] = useState<EditableCard[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [movingId, setMovingId] = useState<number | null>(null);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");

    useEffect(() => {
        fetchCards();
    }, []);

    const fetchCards = async () => {
        try {
            setLoading(true);
            setError("");
            setSuccess("");

            const response = await api.get(
                "/admin/page-feature-cards"
            );

            const allCards: PageFeatureCard[] =
                response.data.data || response.data;

            const homeCards = allCards
                .filter(
                    (card) => card.page?.slug === "home"
                )
                .sort(
                    (a, b) =>
                        a.display_order -
                        b.display_order
                )
                .map((card) => ({
                    ...card,
                    newImage: null,
                    newIcon: null,
                }));

            setCards(homeCards);
        } catch (err: any) {
            console.error(
                "Failed to load Home feature cards:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load Home feature cards. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const updateCard = (
        id: number,
        field: "body" | "link",
        value: string
    ) => {
        setCards((currentCards) =>
            currentCards.map((card) =>
                card.id === id
                    ? {
                        ...card,
                        [field]: value,
                    }
                    : card
            )
        );
    };

    const updateImage = (
        id: number,
        file: File | null
    ) => {
        setCards((currentCards) =>
            currentCards.map((card) =>
                card.id === id
                    ? {
                        ...card,
                        newImage: file,
                    }
                    : card
            )
        );
    };

    const updateIcon = (
        id: number,
        file: File | null
    ) => {
        setCards((currentCards) =>
            currentCards.map((card) =>
                card.id === id
                    ? {
                        ...card,
                        newIcon: file,
                    }
                    : card
            )
        );
    };

    const getFileUrl = (path: string | null) => {
        if (!path) {
            return "";
        }

        if (
            path.startsWith("http://") ||
            path.startsWith("https://")
        ) {
            return path;
        }

        return `${IMAGE_URL}/${path}`;
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError("");
            setSuccess("");

            for (const card of cards) {
                const formData = new FormData();

                formData.append(
                    "body",
                    card.body
                );

                formData.append(
                    "link",
                    card.link || ""
                );

                formData.append(
                    "display_order",
                    String(card.display_order)
                );

                if (card.newImage) {
                    formData.append(
                        "image",
                        card.newImage
                    );
                }

                if (card.newIcon) {
                    formData.append(
                        "icon",
                        card.newIcon
                    );
                }

                formData.append(
                    "_method",
                    "PUT"
                );

                await api.post(
                    `/admin/page-feature-cards/${card.id}`,
                    formData
                );
            }

            await fetchCards();

            setSuccess(
                "Why Safari changes saved successfully."
            );
        } catch (err: any) {
            console.error(
                "Failed to save Home feature cards:",
                err
            );

            const validationErrors =
                err.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(
                    validationErrors
                )
                    .flat()
                    .find(
                        (
                            validationError
                        ): validationError is string =>
                            typeof validationError ===
                            "string"
                    );

                setError(
                    firstError ||
                    "Unable to save changes. Please try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Unable to save changes. Please try again."
                );
            }
        } finally {
            setSaving(false);
        }
    };

    const handleMove = async (
        card: EditableCard,
        direction: "up" | "down"
    ) => {
        try {
            setMovingId(card.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/page-feature-cards/${card.id}/move`,
                {
                    direction,
                }
            );

            await fetchCards();
        } catch (err: any) {
            console.error(
                "Failed to move feature card:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to change card order. Please try again."
            );
        } finally {
            setMovingId(null);
        }
    };

    return (
        <div className="admin-page">

            <AdminHeader />

            <div className="admin-body">

                <AdminSidebar />

                <main className="admin-content">

                    <div className="admin-users">

                        <div className="admin-page-header">

                            <div>
                                <h1>
                                    Home
                                </h1>

                                <p>
                                    Manage the content
                                    displayed on the
                                    Home page.
                                </p>
                            </div>

                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        <div className="admin-content-section">

                            <div className="admin-page-header">

                                <div>
                                    <h2>
                                        Why Safari
                                    </h2>

                                    <p>
                                        Manage the three
                                        feature cards
                                        displayed on the
                                        Home page.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="admin-primary-button"
                                    onClick={handleSave}
                                    disabled={
                                        loading ||
                                        saving ||
                                        movingId !== null
                                    }
                                >
                                    {saving
                                        ? "Saving..."
                                        : "Save changes"}
                                </button>

                            </div>

                            {loading ? (
                                <p>
                                    Loading Home feature
                                    cards...
                                </p>
                            ) : cards.length === 0 ? (
                                <p>
                                    No Home feature cards
                                    found.
                                </p>
                            ) : (
                                <div className="admin-feature-cards">

                                    {cards.map(
                                        (card, index) => (
                                            <div
                                                className="admin-feature-card"
                                                key={card.id}
                                            >

                                                <div className="admin-feature-card-header">

                                                    <div>
                                                        <h3>
                                                            {card.title}
                                                        </h3>

                                                        <span>
                                                            Card {index + 1}
                                                        </span>
                                                    </div>

                                                    <div className="admin-feature-card-order">

                                                        <button
                                                            type="button"
                                                            className="admin-table-button"
                                                            onClick={() =>
                                                                handleMove(
                                                                    card,
                                                                    "up"
                                                                )
                                                            }
                                                            disabled={
                                                                index === 0 ||
                                                                movingId !== null ||
                                                                saving
                                                            }
                                                            aria-label={`Move ${card.title} up`}
                                                        >
                                                            ↑
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="admin-table-button"
                                                            onClick={() =>
                                                                handleMove(
                                                                    card,
                                                                    "down"
                                                                )
                                                            }
                                                            disabled={
                                                                index ===
                                                                cards.length -
                                                                1 ||
                                                                movingId !== null ||
                                                                saving
                                                            }
                                                            aria-label={`Move ${card.title} down`}
                                                        >
                                                            ↓
                                                        </button>

                                                    </div>

                                                </div>

                                                <div className="admin-feature-card-content">

                                                    <div className="form-group">

                                                        <label
                                                            htmlFor={`feature-image-${card.id}`}
                                                        >
                                                            Image
                                                        </label>

                                                        <div className="admin-feature-image-preview">

                                                            {card.newImage ? (
                                                                <img
                                                                    src={URL.createObjectURL(card.newImage)}
                                                                    alt={`${card.title} feature`}
                                                                />
                                                            ) : card.image ? (
                                                                <img
                                                                    src={getFileUrl(card.image)}
                                                                    alt={`${card.title} feature`}
                                                                />
                                                            ) : (
                                                                <span>
                                                                    No image uploaded
                                                                </span>
                                                            )}

                                                        </div>

                                                        <input
                                                            id={`feature-image-${card.id}`}
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                updateImage(
                                                                    card.id,
                                                                    event
                                                                        .target
                                                                        .files?.[0] ||
                                                                    null
                                                                )
                                                            }
                                                            disabled={
                                                                saving
                                                            }
                                                        />

                                                    </div>

                                                    <div className="form-group">

                                                        <label
                                                            htmlFor={`feature-body-${card.id}`}
                                                        >
                                                            Body
                                                        </label>

                                                        <textarea
                                                            id={`feature-body-${card.id}`}
                                                            value={
                                                                card.body
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                updateCard(
                                                                    card.id,
                                                                    "body",
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                            rows={5}
                                                            disabled={
                                                                saving
                                                            }
                                                        />

                                                    </div>

                                                    <div className="form-group">

                                                        <label
                                                            htmlFor={`feature-icon-${card.id}`}
                                                        >
                                                            Icon
                                                        </label>

                                                        {card.icon && (
                                                            <div className="admin-feature-icon-preview">

                                                                <img
                                                                    src={getFileUrl(
                                                                        card.icon
                                                                    )}
                                                                    alt={`${card.title} icon`}
                                                                />

                                                            </div>
                                                        )}

                                                        <input
                                                            id={`feature-icon-${card.id}`}
                                                            type="file"
                                                            accept=".svg,image/svg+xml"
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                updateIcon(
                                                                    card.id,
                                                                    event
                                                                        .target
                                                                        .files?.[0] ||
                                                                    null
                                                                )
                                                            }
                                                            disabled={
                                                                saving
                                                            }
                                                        />

                                                        <small>
                                                            Optional SVG
                                                            icon.
                                                        </small>

                                                    </div>

                                                    <div className="form-group">

                                                        <label
                                                            htmlFor={`feature-link-${card.id}`}
                                                        >
                                                            Link
                                                        </label>

                                                        <input
                                                            id={`feature-link-${card.id}`}
                                                            type="text"
                                                            value={
                                                                card.link ||
                                                                ""
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                updateCard(
                                                                    card.id,
                                                                    "link",
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                            placeholder="Optional"
                                                            disabled={
                                                                saving
                                                            }
                                                        />

                                                    </div>

                                                </div>

                                            </div>
                                        )
                                    )}

                                </div>
                            )}

                        </div>

                    </div>

                </main>

            </div>

        </div>
    );
};

export default AdminHome;
